import { useMemo, type ReactNode } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faClock,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import type {
  AgentSummary,
  MissionDetail,
  MissionLane,
  MissionStage,
  MissionTaskGraphNode,
  MissionTaskSummary,
} from "@/services/api/agentic-v2";
import { Badge } from "@/components/ui/badge/Badge";
import { getAgenticTaskStatusBadgeColor } from "@/components/missions/agenticTaskStatusBadge";
import { MissionControlCard } from "@/components/mission-control/MissionControlCard";
import { WORKBENCH_INSET_PANEL_CLASS } from "@/components/ui/workbench";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import { formatRelativeTime } from "../MissionRow";
import {
  buildRowsByStage,
  type TimelineRow,
} from "./missionTimelineRows";
import { MissionStageOpsMenu } from "./MissionStageOpsMenu";

/**
 * Timeline view of a mission: full template plan grouped by stage with
 * a per-stage lane strip. Spawned tasks render as live cards; unspawned
 * tasks from the template render as greyed "Upcoming" placeholders.
 *
 * Visual language: status circle on the left of each stage header
 * (✓ green for done, filled indigo number for active, hollow grey
 * number for upcoming). Tasks reuse ``MissionControlCard`` so they
 * match Action Queue rows visually.
 */
export interface TimelineTabProps {
  mission: MissionDetail;
  agents: AgentSummary[];
  onTaskClick: (task: MissionTaskSummary) => void;
}

export function TimelineTab({
  mission,
  agents,
  onTaskClick,
}: TimelineTabProps) {
  const rowsByStage = useMemo(() => buildRowsByStage(mission), [mission]);
  const agentById = useMemo(() => {
    const m = new Map<string, AgentSummary>();
    for (const a of agents) m.set(a._id, a);
    return m;
  }, [agents]);
  const descriptionByCatalogueId = useMemo(() => {
    const m = new Map<string, string>();
    for (const node of mission.task_graph ?? []) {
      if (node.catalogue_description) {
        m.set(node.catalogue_id, node.catalogue_description);
      }
    }
    return m;
  }, [mission.task_graph]);

  const attentionTasks = useMemo(
    () => mission.tasks.filter(isAttentionTask),
    [mission.tasks],
  );

  return (
    <div className="space-y-3">
      {attentionTasks.length > 0 && (
        <AttentionBanner tasks={attentionTasks} onTaskClick={onTaskClick} />
      )}
      <div className="space-y-1">
        {mission.stages.map((stage, idx) => (
          <StageSection
            key={stage.name}
            mission={mission}
            stageNumber={idx + 1}
            isLast={idx === mission.stages.length - 1}
            stage={stage}
            rows={rowsByStage[stage.name] ?? []}
            agentById={agentById}
            descriptionByCatalogueId={descriptionByCatalogueId}
            onTaskClick={onTaskClick}
          />
        ))}
      </div>
    </div>
  );
}

const ATTENTION_STATUSES = new Set(["needs_review", "blocked"]);

function isAttentionTask(task: MissionTaskSummary): boolean {
  return ATTENTION_STATUSES.has(task.status);
}

/**
 * Pinned summary of the tasks that need a human right now. Clicking a
 * row opens the task thread directly, so the advisor can act without
 * scanning the full timeline.
 */
function AttentionBanner({
  tasks,
  onTaskClick,
}: {
  tasks: MissionTaskSummary[];
  onTaskClick: (task: MissionTaskSummary) => void;
}) {
  return (
    <div
      data-testid="mission-attention-banner"
      className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3"
    >
      <div className="flex items-center gap-2 text-xs font-medium text-amber-200 mb-2">
        <span
          className="inline-flex h-1.5 w-1.5 rounded-full bg-amber-400"
          aria-hidden
        />
        {tasks.length === 1
          ? "1 task needs your attention"
          : `${tasks.length} tasks need your attention`}
      </div>
      <ul className="space-y-1">
        {tasks.map((task) => (
          <li key={task._id}>
            <button
              type="button"
              onClick={() => onTaskClick(task)}
              data-testid={`mission-attention-task-${task._id}`}
              className="w-full flex items-center gap-2 text-left text-sm text-slate-900 hover:text-slate-700 focus:outline-none focus-visible:ring-1 focus-visible:ring-amber-400/40 rounded px-1 py-0.5 dark:text-slate-100 dark:hover:text-white"
            >
              <span className="truncate flex-1">{task.name}</span>
              <span className="text-[11px] text-amber-300/80 shrink-0 capitalize">
                {task.status.replace(/_/g, " ")}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Row composition ─────────────────────────────────────────────────

/** Horizontal centre of the 28px (``h-7``) stage status / checkmark circle. */
const TREE_RAIL_LEFT = "left-[11px]";
/** ``space-y-1`` between sections (4px) + half a stage circle (14px) — rail extension to the next centre. */
const STAGE_RAIL_BRIDGE_PX = 18;

// ── Stage section ───────────────────────────────────────────────────

interface StageSectionProps {
  mission: MissionDetail;
  stageNumber: number;
  isLast: boolean;
  stage: MissionStage;
  rows: TimelineRow[];
  agentById: Map<string, AgentSummary>;
  descriptionByCatalogueId: Map<string, string>;
  onTaskClick: (task: MissionTaskSummary) => void;
}

/**
 * Visual structure (the tree/rail design):
 *
 *   [○] Stage name
 *    │
 *    ├── [ task card ]
 *    │
 *    ├── [ task card ]
 *    │
 *   [○] Next stage
 *
 * The vertical rail is centred on the stage status circle (``-translate-x-1/2``
 * at ``left-[14px]``) and sits behind circles and task cards. Each task draws
 * its own horizontal elbow from the rail to the card edge.
 */
function StageSection({
  mission,
  stageNumber,
  isLast,
  stage,
  rows,
  agentById,
  descriptionByCatalogueId,
  onTaskClick,
}: StageSectionProps) {
  const isComplete = stage.status === "completed";
  const isActive = stage.status === "active";

  const lanes = useMemo(
    () => [...(stage.lanes ?? [])].sort((a, b) => a.order - b.order),
    [stage.lanes],
  );
  const groupedByLane = useMemo(
    () => groupRowsByLane(rows, lanes),
    [rows, lanes],
  );

  return (
    <section
      data-stage-name={stage.name}
      className="relative"
    >
      {/* Vertical rail — behind stage circles and task cards. */}
      {!isLast ? (
        <span
          aria-hidden
          className={clsx(
            "pointer-events-none absolute top-7 z-0 w-px -translate-x-1/2 bg-slate-700/70",
            TREE_RAIL_LEFT,
          )}
          style={{ bottom: `-${STAGE_RAIL_BRIDGE_PX}px` }}
        />
      ) : null}

      {/* Stage header — quiet section label. The active stage + current
          lane are surfaced in the mission header, so this row is just
          context for the scrolling timeline below. */}
      <div className="relative z-10 flex items-center gap-3 pb-3">
        <StageStatusCircle
          stageNumber={stageNumber}
          isComplete={isComplete}
          isActive={isActive}
        />
        <span
          className={clsx(
            "min-w-0 flex-1 text-sm font-medium leading-tight",
            isActive
              ? "text-slate-100"
              : isComplete
                ? "text-slate-300"
                : "text-slate-500",
          )}
        >
          {stage.display_name}
        </span>
        <MissionStageOpsMenu mission={mission} stage={stage} isLastStage={isLast} />
      </div>

      {/* Tasks */}
      {rows.length > 0 ? (
        <div className="space-y-1.5 pb-3">
          {groupedByLane.map(({ lane, items }) =>
            items.map((row, idx) => (
              <TaskTreeRow
                key={
                  row.kind === "real"
                    ? row.task._id
                    : `upcoming-${row.node.key}-${lane?.key ?? "n"}-${idx}`
                }
                row={row}
                stageDisplayName={stage.display_name}
                agentById={agentById}
                descriptionByCatalogueId={descriptionByCatalogueId}
                onClick={
                  row.kind === "real" ? () => onTaskClick(row.task) : undefined
                }
              />
            )),
          )}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Task card wrapped in the tree elbow: a short horizontal line that
 * branches off the vertical rail into the card's left edge.
 */
function TaskTreeRow({
  row,
  stageDisplayName,
  agentById,
  descriptionByCatalogueId,
  onClick,
}: {
  row: TimelineRow;
  stageDisplayName: string;
  agentById: Map<string, AgentSummary>;
  descriptionByCatalogueId: Map<string, string>;
  onClick?: () => void;
}) {
  const showConnector = row.kind === "real";

  return (
    <div className="relative pl-10">
      {showConnector ? (
        <span
          aria-hidden
          className={clsx(
            "pointer-events-none absolute top-1/2 z-0 h-px w-[26px] -translate-y-1/2 bg-slate-700/70",
            TREE_RAIL_LEFT,
          )}
        />
      ) : null}
      <div className={clsx("relative z-[1] ring-1 ring-slate-200/80 hover:ring-slate-300/80 transition-colors dark:ring-slate-800/80 dark:hover:ring-slate-700/80", WORKBENCH_INSET_PANEL_CLASS)}>
        <TaskRow
          row={row}
          stageDisplayName={stageDisplayName}
          agentById={agentById}
          descriptionByCatalogueId={descriptionByCatalogueId}
          onClick={onClick}
        />
      </div>
    </div>
  );
}

/**
 * Group rows within a stage by lane, preserving lane order. Rows with
 * no recognised ``lane_key`` go into a trailing bucket.
 */
function groupRowsByLane(
  rows: TimelineRow[],
  lanes: MissionLane[],
): { lane: MissionLane | null; items: TimelineRow[] }[] {
  const byKey = new Map<string, TimelineRow[]>();
  const nolane: TimelineRow[] = [];

  const laneKeyOf = (row: TimelineRow): string | null => {
    if (row.kind === "upcoming") return row.node.lane_key ?? null;
    return row.node?.lane_key ?? null;
  };

  for (const row of rows) {
    const key = laneKeyOf(row);
    if (key && lanes.some((l) => l.key === key)) {
      const list = byKey.get(key) ?? [];
      list.push(row);
      byKey.set(key, list);
    } else {
      nolane.push(row);
    }
  }

  const out: { lane: MissionLane | null; items: TimelineRow[] }[] = [];
  for (const lane of lanes) {
    const items = byKey.get(lane.key);
    if (items && items.length) out.push({ lane, items });
  }
  if (nolane.length) out.push({ lane: null, items: nolane });
  return out;
}

// ── Stage status circle ─────────────────────────────────────────────

function StageStatusCircle({
  stageNumber,
  isComplete,
  isActive,
}: {
  stageNumber: number;
  isComplete: boolean;
  isActive: boolean;
}) {
  return (
    <div
      className={clsx(
        "relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        isComplete && "bg-emerald-500/90 text-white",
        isActive && "bg-indigo-500 text-white ring-4 ring-indigo-500/20",
        !isComplete &&
          !isActive &&
          "border border-slate-600/60 bg-slate-700/55 text-slate-500",
      )}
      aria-hidden
    >
      {isComplete ? (
        <FontAwesomeIcon icon={faCheck} className="text-[10px]" />
      ) : (
        stageNumber
      )}
    </div>
  );
}

// ── Task rows ───────────────────────────────────────────────────────

function TaskRow({
  row,
  stageDisplayName,
  agentById,
  descriptionByCatalogueId,
  onClick,
}: {
  row: TimelineRow;
  stageDisplayName: string;
  agentById: Map<string, AgentSummary>;
  descriptionByCatalogueId: Map<string, string>;
  onClick?: () => void;
}) {
  if (row.kind === "upcoming") {
    return <UpcomingTaskRow node={row.node} />;
  }
  return (
    <RealTaskRow
      task={row.task}
      stageDisplayName={stageDisplayName}
      agentById={agentById}
      description={descriptionByCatalogueId.get(row.task.catalogue_id)}
      onClick={onClick}
    />
  );
}

function RealTaskRow({
  task,
  agentById,
  description,
  onClick,
}: {
  task: MissionTaskSummary;
  stageDisplayName: string;
  agentById: Map<string, AgentSummary>;
  description?: string;
  onClick?: () => void;
}) {
  const isDone = task.status === "done" || task.status === "auto_closed";
  const isGated = task.status === "gated" && Boolean(task.gate_not_before);
  const subtitle = renderTaskSubtitle(task, isGated, description);

  return (
    <MissionControlCard
      data-testid={`timeline-task-${task._id}`}
      onClick={onClick}
      compact
      className={clsx(isDone && "opacity-60")}
      icon={
        <WorkerAvatar
          seed={
            task.assigned_agent_id
              ? agentById.get(task.assigned_agent_id)?.name ?? task.name
              : task.name
          }
          size={24}
          user={task.task_type === "human"}
        />
      }
      title={task.name}
      subtitle={subtitle}
      status={<TaskStatusPill status={task.status} />}
      timestamp={
        <span className="text-slate-500">
          {formatRelativeTime(task.updated_at)}
        </span>
      }
    />
  );
}

function UpcomingTaskRow({ node }: { node: MissionTaskGraphNode }) {
  return (
    <MissionControlCard
      data-testid={`timeline-upcoming-${node.key}`}
      compact
      className="opacity-50"
      icon={
        <div className="h-6 w-6 rounded-full border border-dashed border-slate-700" />
      }
      title={
        <span className="text-slate-400">{node.catalogue_display_name}</span>
      }
      subtitle={
        node.catalogue_description ? (
          <div className="mt-0.5 text-xs text-slate-500 truncate">
            {node.catalogue_description}
          </div>
        ) : null
      }
    />
  );
}

const PILL_CLASS = "!normal-case !tracking-normal !font-sans whitespace-nowrap";

/**
 * Render task status with two visual tiers:
 *
 *   - Default lifecycle (queued / working / done / scheduled) → small
 *     dot + muted label. Calm, scannable, doesn't compete with names.
 *   - Attention (needs_review / blocked / anything else) → coloured
 *     pill so it visibly stands out against the calm rows.
 */
function TaskStatusPill({ status }: { status: string }) {
  const calm = CALM_STATUS_DISPLAY[status];
  if (calm) {
    return (
      <span
        className={clsx(
          "inline-flex items-center gap-1.5 text-[11px] whitespace-nowrap",
          calm.textClass,
        )}
      >
        <span
          aria-hidden
          className={clsx("h-1.5 w-1.5 rounded-full", calm.dotClass)}
        />
        {calm.label}
      </span>
    );
  }
  return (
    <Badge
      color={getAgenticTaskStatusBadgeColor(status)}
      size="sm"
      className={PILL_CLASS}
    >
      {status.replace(/_/g, " ")}
    </Badge>
  );
}

interface CalmStatus {
  label: string;
  textClass: string;
  dotClass: string;
}

const CALM_STATUS_DISPLAY: Record<string, CalmStatus> = {
  queued: {
    label: "Queued",
    textClass: "text-slate-500",
    dotClass: "bg-slate-600",
  },
  assigned: {
    label: "Queued",
    textClass: "text-slate-500",
    dotClass: "bg-slate-600",
  },
  working: {
    label: "Working",
    textClass: "text-indigo-300/90",
    dotClass: "bg-indigo-400 animate-pulse",
  },
  in_progress: {
    label: "Working",
    textClass: "text-indigo-300/90",
    dotClass: "bg-indigo-400 animate-pulse",
  },
  running: {
    label: "Working",
    textClass: "text-indigo-300/90",
    dotClass: "bg-indigo-400 animate-pulse",
  },
  done: {
    label: "Done",
    textClass: "text-emerald-400/80",
    dotClass: "bg-emerald-500/70",
  },
  completed: {
    label: "Done",
    textClass: "text-emerald-400/80",
    dotClass: "bg-emerald-500/70",
  },
  auto_closed: {
    label: "Done",
    textClass: "text-slate-500",
    dotClass: "bg-slate-600",
  },
  gated: {
    label: "Scheduled",
    textClass: "text-sky-300/80",
    dotClass: "bg-sky-400/70",
  },
};

/**
 * Choose the most useful subtitle line for a task row, in priority:
 *   1. Blocked banner / reason
 *   2. Gated "opens X" copy
 *   3. Latest agent summary
 *   4. Nothing — keep the row tight
 */
function renderTaskSubtitle(
  task: MissionTaskSummary,
  isGated: boolean,
  description?: string,
): ReactNode {
  const display = task.gate_blocked_display;
  const blockedCopy =
    display?.body ??
    display?.title ??
    (task.gate_blocked_reason
      ? `${task.gate_blocked_reason.path} (${task.gate_blocked_reason.op})`
      : null);
  if (task.status === "blocked" && blockedCopy) {
    return (
      <div className="mt-0.5 text-xs text-amber-300/80 truncate">
        {blockedCopy}
      </div>
    );
  }
  if (isGated && task.gate_not_before) {
    return (
      <div className="mt-0.5 space-y-0.5">
        <div className="flex items-center gap-1 text-xs text-slate-400">
          <FontAwesomeIcon icon={faClock} className="text-[10px]" aria-hidden />
          <span>Opens {formatGateDate(task.gate_not_before)}</span>
        </div>
        {description ? (
          <div className="text-xs text-slate-500 truncate">{description}</div>
        ) : null}
      </div>
    );
  }
  if (task.latest_summary) {
    return (
      <div className="mt-0.5 text-xs text-slate-400 truncate">
        {task.latest_summary}
      </div>
    );
  }
  if (description) {
    return (
      <div className="mt-0.5 text-xs text-slate-500 truncate">
        {description}
      </div>
    );
  }
  return null;
}

function formatGateDate(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}
